import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';
import { formatDataForDataTable } from '../../utils/formatData';

const initialState = {
  loading: false,
  data: [],
  feeForecast: [],
  forecastConfig: [],
  logs: [],
  crmList: [],
  manualForecast: [],
  crmUnderdept: [],
  crmUnderdeptLoading: false,
  summary: [],
  expectedSessionLoading: false,
  jobStatus: null,
};

export const getFeeForecastData = createAsyncThunk('feeForecast/getFeeForecastData', async (data, action) => {
  const res = await apiRequest({
    url: `fee-forecast/admission-register?page=${data.page}&limit=${data.limit}&date=${data.date}&search=${data.search}&sortKey=${data.sortKey}&sortDir=${data.sortDir}&deptId=${data.deptId}&crm=${data.crm}&cro=${data.cro}&forecast_typeName=${data.forecast_typeName}&final_source=${data.final_source}&currency_type=${data.currency_type}&threshold=${data.threshold}&course_status=${data.course_status}&override_status=${data.override_status || ''}&hardRefresh=${data.hardRefresh}&isBulkExpectedSessions=${data.isBulkExpectedSessions ? data.isBulkExpectedSessions : null}`,
    method: 'get',
  });
  if (data.limit == 'no') {
    return action.rejectWithValue(res);
  }
  return res;
});

export const addExpectedSessions = createAsyncThunk('feeForecast/addExpectedSessions', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/expected-sessions`,
    method: 'post',
    data: data,
  });
  return res;
});

export const getForecastConfig = createAsyncThunk('feeForecast/getForecastConfig', async () => {
  const res = await apiRequest({
    url: `fee-forecast/config`,
    method: 'get',
  });
  return res;
});

export const updateForecastConfig = createAsyncThunk('feeForecast/updateForecastConfig', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/config`,
    method: 'post',
    data: data,
  });
  return res;
});

export const getLogs = createAsyncThunk('feeForecast/getLogs', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/fee-logs?year=${data.year}&month=${data.month}&page=${data.page}&limit=${data.limit}&search=${data.search}&sortDir=${data.sortDir}&sortKey=${data.sortKey}&project=${data.project}`,
    method: 'get',
  });
  return res;
});

export const addLogs = createAsyncThunk('feeForecast/addLogs', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/fee-logs`,
    method: 'post',
    data: data,
  });
  return res;
});

export const getCrms = createAsyncThunk('feeForecast/getCrms', async (projectId) => {
  const res = await apiRequest({
    url: `fee-forecast/crms?projectId=${projectId}`,
    method: 'get',
  });
  return res;
});

export const getCrmsUnderDept = createAsyncThunk('feeForecast/getCrmsUnderDept', async (deptId) => {
  const res = await apiRequest({
    url: `general/get-crms-underADept/${deptId}`,
    method: 'get',
  });
  return res;
});

export const getManualForecast = createAsyncThunk('feeForecast/getManualForecast', async (projectId) => {
  const res = await apiRequest({
    url: `fee-forecast/manual-forecast`,
    method: 'get',
  });
  return res;
});

export const bulkUpload = createAsyncThunk('feeForecast/bulkUpload', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/manual-forecast`,
    method: 'post',
    data: data,
  });
  return res;
});
export const getSummary = createAsyncThunk('feeForecast/getSummary', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/summary-cards?date=${data.date}&search=${data.search || ''}&deptId=${data.deptId}&crm=${data.crm}&cro=${data.cro}&forecast_typeName=${data.forecast_typeName}&final_source=${data.final_source}&currency_type=${data.currency_type}&threshold=${data.threshold}&course_status=${data.course_status}&override_status=${data.override_status || ''}&hardRefresh=${data.hardRefresh}`,
    method: 'get',
  });
  return res;
});

export const UpdateBulkExpectedSessions = createAsyncThunk('feeForecast/UpdateBulkExpectedSessions', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/bulk-expected-sessions`,
    method: 'post',
    data: data,
  });
  return res;
});

export const overrideForecastAmount = createAsyncThunk('feeForecast/overrideForecastAmount', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/override-amount`,
    method: 'post',
    data: data,
  });
  return res;
});

export const approveForecastOverride = createAsyncThunk('feeForecast/approveForecastOverride', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/approve-override`,
    method: 'post',
    data: data,
  });
  return res;
});

export const recalculateForecastFee = createAsyncThunk('feeForecast/recalculateForecastFee', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/recalculate-fee`,
    method: 'post',
    data: data,
  });
  return res;
});

export const triggerFullSync = createAsyncThunk('feeForecast/triggerFullSync', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/trigger-full-sync?date=${data?.date || ''}`,
    method: 'get',
  });
  return res;
});

export const triggerBulkManualForecast = createAsyncThunk('feeForecast/triggerBulkManualForecast', async (data) => {
  const res = await apiRequest({
    url: `fee-forecast/bulk-manual-forecast-trigger?date=${data?.date || ''}`,
    method: 'post',
  });
  return res;
});

export const getForecastJobStatus = createAsyncThunk('feeForecast/getForecastJobStatus', async () => {
  const res = await apiRequest({
    url: `fee-forecast/job-status`,
    method: 'get',
  });
  return res;
});

const feeForecastSlice = createSlice({
  name: 'feeForecastSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //.................createCartItem..............................
    builder.addCase(getFeeForecastData.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getFeeForecastData.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        feeForecast: action.payload,
      };
    });
    builder.addCase(getFeeForecastData.rejected, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------addExpectedSessions-----------------------------------
    builder.addCase(addExpectedSessions.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(addExpectedSessions.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // --------------getForecastConfig--------------------------------------

    builder.addCase(getForecastConfig.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getForecastConfig.fulfilled, (state, action) => {
      return {
        ...state,
        forecastConfig: action.payload.data,
        loading: false,
      };
    });
    // -----------------updateForecastConfig------------------------

    builder.addCase(updateForecastConfig.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(updateForecastConfig.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ------------------getLogs----------------------------------

    builder.addCase(getLogs.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getLogs.fulfilled, (state, action) => {
      return {
        ...state,
        logs: action.payload,
        loading: false,
      };
    });
    // -----------------addLogs-------------------------------
    builder.addCase(addLogs.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(addLogs.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------getCrms---------------------------------

    builder.addCase(getCrms.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getCrms.fulfilled, (state, action) => {
      return {
        ...state,
        crmList: action.payload.data,
        loading: false,
      };
    });

    // --------------getManualForecast---------------------------
    builder.addCase(getManualForecast.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getManualForecast.fulfilled, (state, action) => {
      return {
        ...state,
        manualForecast: action.payload.data,
        loading: false,
      };
    });
    // -------------------bulkUpload----------------------------
    builder.addCase(bulkUpload.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(bulkUpload.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // --------------------getCrmsUnderDept-------------------------------------

    builder.addCase(getCrmsUnderDept.pending, (state, action) => {
      return {
        ...state,
        crmUnderdeptLoading: true,
      };
    });
    builder.addCase(getCrmsUnderDept.fulfilled, (state, action) => {
      return {
        ...state,
        crmUnderdept: action.payload.data,
        crmUnderdeptLoading: false,
      };
    });
    // -----------------getSummary--------------------------------

    builder.addCase(getSummary.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getSummary.fulfilled, (state, action) => {
      return {
        ...state,
        summary: action.payload.data,
        loading: false,
      };
    });
    // -----------------UpdateBulkExpectedSessions-------------------------------
    builder.addCase(UpdateBulkExpectedSessions.pending, (state, action) => {
      return {
        ...state,
        expectedSessionLoading: true,
      };
    });
    builder.addCase(UpdateBulkExpectedSessions.fulfilled, (state, action) => {
      return {
        ...state,
        expectedSessionLoading: false,
      };
    });
    // -----------------getForecastJobStatus-------------------------------
    builder.addCase(getForecastJobStatus.fulfilled, (state, action) => {
      return {
        ...state,
        jobStatus: action.payload.data,
      };
    });
  },
});

export default feeForecastSlice.reducer;
